import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Spin, 
  message, 
  Empty, 
  Statistic, 
  Progress, 
  Button, 
  Space, 
  List, 
  Avatar, 
  Typography, 
  Table, 
  Tag 
} from 'antd';
import {
  UserOutlined,
  FileTextOutlined,
  DollarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import './index.css';
import {
  getDayDetail,
  getWeekStats,
  getMonthStats,
  DashboardDayDetail,
  DashboardWeekStats,
  DashboardMonthStats,
} from '../../services/dashboard';
import { getUserInfo } from '../../utils/auth';
import CreateLeadModal from '../../components/LeadDrawer/CreateLeadModal';
import CaseWizard from '../Case/components/CaseWizard';

dayjs.locale('zh-cn');

const { Text, Title } = Typography;

interface TodayTask {
  id: number;
  target_type: 'lead' | 'case';
  target_id: number;
  target_name: string;
  next_follow_up_time: string;
  last_follow_up_time: string | null;
  follow_status: string | null;
}

const Workspace: React.FC = () => {
  const navigate = useNavigate();
  const userInfo = getUserInfo();
  const [loading, setLoading] = useState(true);
  const [weekStats, setWeekStats] = useState<DashboardWeekStats | null>(null);
  const [monthStats, setMonthStats] = useState<DashboardMonthStats | null>(null);
  const [todayTasks, setTodayTasks] = useState<TodayTask[]>([]);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showCaseWizard, setShowCaseWizard] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadWeekStats(),
        loadMonthStats(),
        loadTodayTasks(),
      ]);
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWeekStats = async () => {
    try {
      const data = await getWeekStats();
      setWeekStats(data);
    } catch (error) {
      console.error('加载本周统计失败:', error);
    }
  };

  const loadMonthStats = async () => {
    try {
      const data = await getMonthStats();
      setMonthStats(data);
    } catch (error) {
      console.error('加载本月统计失败:', error);
    }
  };

  const loadTodayTasks = async () => {
    try {
      const today = dayjs().format('YYYY-MM-DD');
      const data = await getDayDetail(today);
      setTodayTasks(data.items.map(item => ({
        id: item.id,
        target_type: item.target_type as 'lead' | 'case',
        target_id: item.target_id,
        target_name: item.target_name,
        next_follow_up_time: item.next_follow_up_time,
        last_follow_up_time: item.last_follow_up_time,
        follow_status: item.follow_status,
      })));
    } catch (error) {
      console.error('加载今日待办失败:', error);
    }
  };

  // 格式化上次跟进时间（参考线索管理的逻辑）
  const formatLastFollowTime = (time: string | null): string => {
    if (!time) return '-';
    
    const now = dayjs();
    const followTime = dayjs(time);
    const days = now.diff(followTime, 'day');
    
    if (days === 0) return '今天';
    if (days === 1) return '1天前';
    if (days === 2) return '2天前';
    if (days === 3) return '3天前';
    if (days < 7) return `${days}天前`;
    if (days < 14) return '1周前';
    if (days < 30) return '半个月前';
    if (days < 60) return '1个月前';
    if (days < 90) return '2个月前';
    if (days < 180) return '3个月前';
    if (days < 365) return '半年前';
    
    const years = Math.floor(days / 365);
    return `${years}年前`;
  };

  const handleGoToDetail = (task: TodayTask) => {
    // 跳转到对应页面并通过 URL 参数传递要打开的详情 ID
    if (task.target_type === 'lead') {
      navigate(`/leads?openDetail=${task.target_id}`);
    } else {
      navigate(`/cases?openDetail=${task.target_id}`);
    }
  };

  const taskColumns = [
    {
      title: '类型',
      dataIndex: 'target_type',
      key: 'target_type',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'lead' ? 'blue' : 'green'}>
          {type === 'lead' ? '线索' : '案件'}
        </Tag>
      ),
    },
    {
      title: '名称',
      dataIndex: 'target_name',
      key: 'target_name',
      render: (name: string, record: TodayTask) => (
        <Text>{record.target_type === 'lead' ? `联系人：${name}` : name}</Text>
      ),
    },
    {
      title: '跟进意向',
      dataIndex: 'follow_status',
      key: 'follow_status',
      width: 120,
      render: (status: string | null, record: TodayTask) => (
        <Text>{record.target_type === 'case' ? '-' : (status || '-')}</Text>
      ),
    },
    {
      title: '上次跟进',
      dataIndex: 'last_follow_up_time',
      key: 'last_follow_up_time',
      width: 120,
      render: (time: string | null) => (
        <Text>{formatLastFollowTime(time)}</Text>
      ),
    },
    {
      title: '下次跟进时间',
      dataIndex: 'next_follow_up_time',
      key: 'next_follow_up_time',
      width: 150,
      render: (time: string) => (
        <Text>{dayjs(time).format('YYYY-MM-DD HH:mm')}</Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: any, record: TodayTask) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleGoToDetail(record)}
        >
          去跟进
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="workspace-container">
      {/* 欢迎语 */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          欢迎回来，{userInfo?.real_name || userInfo?.name || userInfo?.username}！
        </Title>
        <Text type="secondary">
          今天是 {dayjs().format('YYYY年MM月DD日 dddd')}
        </Text>
      </div>

      {/* 顶部统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card hoverable className="stat-card">
            <Statistic
              title="今日待办"
              value={weekStats?.today_pending_count || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable className="stat-card">
            <Statistic
              title="本周已跟进"
              value={weekStats?.week_followed_count || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable className="stat-card">
            <Statistic
              title="本周新增线索"
              value={weekStats?.week_new_leads_count || 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable className="stat-card">
            <Statistic
              title="本周新增案件"
              value={weekStats?.week_new_cases_count || 0}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 本月进度概览和快捷操作 */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card title="本月进度概览" className="progress-card">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">案件完成率</Text>
                  <Progress
                    type="circle"
                    percent={Math.round(monthStats?.case_completion_rate || 0)}
                    format={(percent) => `${percent}%`}
                    size={120}
                    strokeColor="#52c41a"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">线索转化率</Text>
                  <Progress
                    type="circle"
                    percent={Math.round(monthStats?.lead_conversion_rate || 0)}
                    format={(percent) => `${percent}%`}
                    size={120}
                    strokeColor="#1890ff"
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="快捷操作" className="quick-actions-card">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Button
                type="primary"
                block
                icon={<UserOutlined />}
                onClick={() => setShowLeadModal(true)}
              >
                新增线索
              </Button>
              <Button
                block
                icon={<FileTextOutlined />}
                onClick={() => setShowCaseWizard(true)}
              >
                新增案件
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 今日待办事项 */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card 
            title={`今日待办事项 (${todayTasks.length})`}
            className="tasks-card"
          >
            <Table
              dataSource={todayTasks}
              columns={taskColumns}
              pagination={false}
              size="middle"
              rowKey="id"
              locale={{ emptyText: <Empty description="今天没有待办事项" /> }}
            />
          </Card>
        </Col>
      </Row>

      {/* 新增线索弹窗 */}
      <CreateLeadModal
        visible={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSuccess={() => {
          setShowLeadModal(false);
          loadAllData();
        }}
      />

      {/* 新增案件向导 */}
      <CaseWizard
        visible={showCaseWizard}
        mode="create"
        onClose={() => setShowCaseWizard(false)}
        onSuccess={() => {
          setShowCaseWizard(false);
          loadAllData();
        }}
      />
    </div>
  );
};

export default Workspace;
